// src/components/cycles/CycleMenu.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Typography,
  message,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CycleViewModal from "./CycleViewModal";
import Constants from "../Utils/Constants";

const { Title, Text, Paragraph } = Typography;

const api = axios.create({
  baseURL: Constants.SERVER_URL,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const API = {
  list: "/cycles",
  create: "/create-cycle",
  remove: (key) => `/delete-cycle/${encodeURIComponent(key)}`,
};

export default function CycleMenu() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [q, setQ] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedCycleKey, setSelectedCycleKey] = useState(null);

  async function fetchCycles() {
    setLoading(true);

    try {
      const { data } = await api.get(API.list);
      setCycles(Array.isArray(data) ? data : data?.items || []);
    } catch (error) {
      console.error(error);
      message.error("Impossible de charger les cycles.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCycles();
  }, []);

  async function handleCreate(values) {
    setCreating(true);

    try {
      const { status, data } = await api.post(API.create, {
        cycleName: values.cycleName.trim(),
        cycleDescription: values.cycleDescription?.trim() || "",
      });

      if (status === 201 && data?.message === "cycle_created_success") {
        message.success("Cycle créé.");

        setCreateOpen(false);
        form.resetFields();

        if (data?.key) {
          nav(`/cycles/${data.key}`);
          return;
        }

        fetchCycles();
        return;
      }

      message.warning(data?.message || "Cycle créé, mais réponse inattendue.");
      setCreateOpen(false);
      form.resetFields();
      fetchCycles();
    } catch (error) {
      const msgTitle = error?.response?.data?.messageTitle;

      if (msgTitle === "cycle_error_same_name") {
        message.error("Nom déjà utilisé. Choisis un autre nom.");
      } else if ([401, 403].includes(error?.response?.status)) {
        message.error("Non autorisé. Connecte-toi d’abord.");
      } else {
        console.error(error);
        message.error("Impossible de créer le cycle.");
      }
    } finally {
      setCreating(false);
    }
  }

  async function onDelete(key) {
    try {
      await api.delete(API.remove(key));
      message.success("Cycle supprimé.");
      fetchCycles();
    } catch (error) {
      console.error(error);
      message.error("Suppression impossible.");
    }
  }

  const filtered = useMemo(() => {
    const search = q.trim().toLowerCase();

    if (!search) return cycles;

    return cycles.filter((cycle) => {
      const name = String(cycle?.name || "").toLowerCase();
      const description = String(cycle?.description || "").toLowerCase();

      return name.includes(search) || description.includes(search);
    });
  }, [q, cycles]);

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space
        align="center"
        style={{
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Cycles
        </Title>

        <Space>
          <Input.Search
            allowClear
            placeholder="Rechercher un cycle…"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            style={{ width: 320 }}
          />

          <Tooltip title="Rafraîchir">
            <Button icon={<ReloadOutlined />} onClick={fetchCycles} />
          </Tooltip>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            Nouveau cycle
          </Button>
        </Space>
      </Space>

      {filtered.length === 0 ? (
        <Card loading={loading}>
          <Empty description="Aucun cycle" />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map((cycle) => {
            const key = cycle.key || cycle.id;

            return (
              <Col key={key} xs={24} sm={12} md={8} lg={6} xl={6}>
                <Card
                  hoverable
                  loading={loading}
                  style={{
                    borderRadius: 14,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    height: 190,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                  bodyStyle={{ padding: "14px 16px" }}
                >
                  <Space
                    direction="vertical"
                    size={6}
                    style={{ width: "100%" }}
                  >
                    <Text strong ellipsis title={cycle.name}>
                      {cycle.name || "Cycle sans nom"}
                    </Text>

                    <Paragraph
                      type="secondary"
                      ellipsis={{ rows: 3 }}
                      style={{ marginBottom: 0 }}
                    >
                      {cycle.description || "Aucune description"}
                    </Paragraph>
                  </Space>

                  <div
                    style={{
                      borderTop: "1px solid #f0f0f0",
                      marginTop: 12,
                      paddingTop: 8,
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title="Modifier">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => nav(`/cycles/${key}`)}
                      />
                    </Tooltip>

                    <Tooltip title="Voir le résumé">
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setSelectedCycleKey(key);
                          setViewOpen(true);
                        }}
                      />
                    </Tooltip>

                    <Popconfirm
                      title="Supprimer ce cycle ?"
                      okText="Supprimer"
                      cancelText="Annuler"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onDelete(key)}
                    >
                      <Tooltip title="Supprimer">
                        <Button type="text" danger icon={<DeleteOutlined />} />
                      </Tooltip>
                    </Popconfirm>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal
        title="Créer un cycle"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        okText="Créer"
        confirmLoading={creating}
        onOk={() => form.submit()}
        maskClosable={false}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            label="Nom du cycle"
            name="cycleName"
            rules={[{ required: true, message: "Nom requis" }]}
          >
            <Input placeholder="Nom du cycle" />
          </Form.Item>

          <Form.Item label="Description" name="cycleDescription">
            <Input.TextArea
              placeholder="Description"
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <CycleViewModal
        open={viewOpen}
        cycleKey={selectedCycleKey}
        onClose={() => {
          setViewOpen(false);
          setSelectedCycleKey(null);
        }}
      />
    </Space>
  );
}